import { PolygonPlaneMeshProps } from '@editor/interfaces/interfaces'
import { DoubleSide } from 'three'

import Label from '../Label'
import Anchor from '../Polyline/components/Anchor'
import Outline from './components/Outline'
import useConfigs from './hooks/useConfigs'
import useEventHandlers from './hooks/useEventHandlers'
import useObjects from './hooks/useObjects'

export const PolygonPlaneMeshV2 = (props: PolygonPlaneMeshProps) => {
  const {
    id,
    name,
    polygon,
    transparent = true,

    color,
    opacity = 1,

    label,

    selectable = false,
    depthTest = true,
    depthWrite = true,
    renderOrder,

    vertexAnchors,
    anchorProps,
  } = props

  // -- Hooks
  const {
    dragStartPosRef,
    currentPositionsRef,
    isDraggingRef,
    handleMouseEnter,
    handleMouseLeave,
    handleMouseDown,
    handleClick,
  } = useEventHandlers(props)
  const { isHoverSelected, labelConfig, guideLabelConfig } = useConfigs(props)
  const {
    offsetPosition,
    offsetQuaternion,

    planePoints,
    planePointsArr,

    geometry,
    guideLabels,

    labelPosition,
    labelText,

    meshRef,
    editingAnchorRef,

    isVisible,
  } = useObjects(props, currentPositionsRef, dragStartPosRef, isDraggingRef)

  return (
    <group visible={isVisible} position={offsetPosition} quaternion={offsetQuaternion}>
      <Outline {...props} renderOrder={renderOrder ? renderOrder + 10 : undefined} positions={planePointsArr} />

      {vertexAnchors && planePoints
        ? planePoints.map(({ id: anchorId, point }, anchorIndex) => (
            <Anchor
              {...vertexAnchors}
              {...anchorProps}
              key={anchorId}
              id={anchorId}
              parentId={id}
              point={point.toArray()}
              anchorIndex={anchorIndex}
              visible={vertexAnchors.visible}
              depthTest={vertexAnchors.depthTest}
              depthWrite={vertexAnchors.depthWrite}
              isParentHoverSelected={isHoverSelected}
              renderOrder={renderOrder ? renderOrder + 20 : undefined}
              onDown={(anchorIdx, event) => {
                if (editingAnchorRef.current === null) {
                  editingAnchorRef.current = anchorIdx
                }
                vertexAnchors.onDown?.(anchorIdx, event)
              }}
              onUpdate={(idx, pt) => {
                const pts = currentPositionsRef.current?.slice() || planePoints.map(({ point: p }) => p.toArray())
                pts[idx] = pt
                vertexAnchors.onUpdate?.(pts)
              }}
              onComplete={() => {
                if (currentPositionsRef.current) {
                  vertexAnchors.onUpdated?.(currentPositionsRef.current)
                }

                editingAnchorRef.current = null
                currentPositionsRef.current = null
              }}
            />
          ))
        : null}

      {/* Plane mesh */}
      {/* Use white when set as transparent since 'transparent' is not a valid color */}
      <mesh
        ref={meshRef}
        onPointerOver={selectable && isVisible ? handleMouseEnter : undefined}
        onPointerOut={handleMouseLeave} // always allow pointer out to reset hover state
        onPointerDown={selectable && isVisible ? handleMouseDown : undefined}
        onClick={selectable && isVisible ? handleClick : undefined}
        geometry={geometry}
        visible={!color || color !== 'transparent'}
        renderOrder={renderOrder}
        name={name ?? id}
        userData={{ shapeId: polygon.shape_id }}
      >
        <meshBasicMaterial
          side={DoubleSide}
          transparent={transparent}
          color={!color || color === 'transparent' ? 'white' : color}
          opacity={opacity}
          depthTest={depthTest}
          depthWrite={depthWrite}
        />
      </mesh>

      {/* Guide line labels */}
      {guideLabelConfig && guideLabels.length
        ? guideLabels.map((labelProps) => (
            <Label
              key={labelProps.id}
              {...labelProps}
              visible={(labelProps.visible ?? true) && isVisible}
              labelPrefix=""
            />
          ))
        : null}

      {/* Label */}
      {!!label && labelConfig ? (
        <Label
          id={`${id}-distance-label`}
          point={labelPosition.toArray()}
          label={labelText || label.label || labelConfig.label}
          labelPrefix=""
          visible={labelConfig.visible && isVisible}
          styles={labelConfig.styles}
          isAdjustingColors={labelConfig.isAdjustingColors}
        />
      ) : null}
    </group>
  )
}
